import { useRef, useState } from 'react'
import type { MouseEvent, PointerEvent } from 'react'
import { Link2 } from 'lucide-react'
import { NetworkNodeIcon } from '@/components/icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { ChatLinkType } from '@/types'

const linkConfig = {
  Workflow: { label: 'Linked to a workflow', Icon: NetworkNodeIcon },
  Chain: { label: 'Linked to a chain', Icon: Link2 },
}

interface ChatLinkIndicatorProps {
  linkType: ChatLinkType
  selected: boolean
}

export function ChatLinkIndicator({ linkType, selected }: Readonly<ChatLinkIndicatorProps>) {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const isTouchRef = useRef(false)

  const config = linkConfig[linkType as keyof typeof linkConfig]
  if (!config) return null

  const { label, Icon } = config

  const handlePointerDown = (e: PointerEvent) => {
    isTouchRef.current = e.pointerType === 'touch'
    if (isTouchRef.current) e.preventDefault()
  }

  const handleClick = (e: MouseEvent) => {
    if (!isTouchRef.current) return
    e.preventDefault()
    e.stopPropagation()
    setTooltipOpen((prev) => !prev)
  }

  return (
    <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
      <TooltipTrigger asChild onPointerDown={handlePointerDown} onClick={handleClick}>
        <span
          role="img"
          aria-label={label}
          className={cn(
            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground',
            selected && 'ring-1 ring-primary-foreground'
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <span className="text-sm font-medium">{label}</span>
      </TooltipContent>
    </Tooltip>
  )
}
